import React from "react";
import { Button } from "antd";
import type { ButtonProps } from "antd";
import { useNavigate } from "react-router-dom";

interface LinkButtonProps extends Omit<ButtonProps, "href"> {
  /** In-app route the button points to (rendered as a real `href`). */
  to: string;
  /** Optional router state forwarded on a plain left-click navigation. */
  state?: unknown;
}

// An antd Button rendered as a real `<a href>` so middle-click, Cmd/Ctrl+click
// and "copy link address" work natively (open a new tab / copy the URL), while
// a plain left-click is intercepted and handled as an in-app SPA navigation —
// preserving router state. Mirrors react-router `<Link>` semantics with antd
// Button styling (the pattern established by ContentMap/NodeTitle).
const LinkButton: React.FC<LinkButtonProps> = ({
  to,
  state,
  onClick,
  ...rest
}) => {
  const navigate = useNavigate();

  return (
    <Button
      {...rest}
      href={to}
      onClick={(e) => {
        onClick?.(e);
        if (e.defaultPrevented) return;
        // Let the browser handle new-tab / new-window intents natively.
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) {
          return;
        }
        e.preventDefault();
        if (state !== undefined) {
          navigate(to, { state });
        } else {
          navigate(to);
        }
      }}
    />
  );
};

export default LinkButton;
